import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useMemo, useState } from "react";
import {
  CreateAssignSpecialPermission,
  FetchSpecialPermission,
} from "services";
import { useAssigmentSpecialPermissionStore } from "../../store";
import { RequestAssigmentSpecialPermission, Selectedpage } from "interfaces";
import { useAssignmentSpecialPermissionList } from "../useAssignmentSpecialPermissionList";
import { useTranslation } from "react-i18next";
import { NotificationType, showNotifications } from "helpers/toas_notification";

interface PermissionState {
  permissionId: number;
  specialPermissionId: number;
  enabled: boolean;
}

export const useModalAssigmentSpecialPermission = () => {
  const { item, setIsOpenModal, clearAll } = useAssigmentSpecialPermissionStore();
  const { refetchAssigmentSpecialPermission } = useAssignmentSpecialPermissionList();
  const { data: SpecialPermissions } = useQuery({
    queryKey: ["special-permissions"],
    queryFn: FetchSpecialPermission,
  });
  
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [permissions, setPermissions] = useState<PermissionState[]>([]);
  const [initialPermissions, setInitialPermissions] = useState<PermissionState[]>([]);
  const [selectedPermission, setSelectedPermission] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const { t } = useTranslation();

  const isFormDisabled = useMemo(() => {
    return !permissions.some((perm, index) => 
      perm.enabled !== initialPermissions[index]?.enabled
    );
  }, [permissions, initialPermissions]);

  const filteredPermissions = useMemo(() => 
    item?.user.role.permissions.filter((permission) =>
      permission.option.description
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    ) || [], 
    [item?.user.role.permissions, searchTerm]
  );

  useEffect(() => {
    if (item && SpecialPermissions?.data && item.user.role.permissions.length) {
      const assignedPermissions = item.user.assignmentpermissionsspecials;

      const initialPerms = item.user.role.permissions.flatMap(
        (permission) =>
          SpecialPermissions?.data.map((special) => {
            const isAssigned = assignedPermissions.some(
              (assigned) =>
                assigned.permissions.id === permission.id &&
                assigned.specialpermissions.id === special.id
            );

            return {
              permissionId: permission.id,
              specialPermissionId: special.id,
              enabled: isAssigned,
            };
          })
      );

      setPermissions(initialPerms);
      setInitialPermissions(initialPerms);

      if (item.user.role.permissions.length > 0) {
        setSelectedPermission(item.user.role.permissions[0].id);
      }
    }
  }, [item, SpecialPermissions]);

  const handleSwitchChange = (
    permissionId: number,
    specialPermissionId: number,
    value: boolean
  ) => {
    setPermissions((prev) =>
      prev.map((perm) =>
        perm.permissionId === permissionId &&
        perm.specialPermissionId === specialPermissionId
          ? { ...perm, enabled: value }
          : perm
      )
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);

      const changedPermissions = permissions.filter((perm, index) => 
        perm.enabled !== initialPermissions[index].enabled
      );

      const groupedPermissions = changedPermissions.reduce((valuesGroup, perm) => {
        const state = perm.enabled ? 1 : 0;
        const existingGroup = valuesGroup.find(
          (group) => group.permissions_id === perm.permissionId && group.state === state
        );

        if (existingGroup) {
          existingGroup.specialpermissions_id.push(perm.specialPermissionId);
        } else {
          valuesGroup.push({
            permissions_id: perm.permissionId,
            specialpermissions_id: [perm.specialPermissionId],
            state: state,
          });
        }

        return valuesGroup;
      }, [] as Selectedpage[]);

      const formattedData: RequestAssigmentSpecialPermission = {
        user_id: item?.user.id || 0,
        selectedpages: groupedPermissions,
      };

      const response = await CreateAssignSpecialPermission(formattedData);

      if (response.code === "200") {
        setIsOpenModal(false);
        refetchAssigmentSpecialPermission();
        setPermissions([]);
        setInitialPermissions([]);
        setSelectedPermission(null);
        showNotifications(
          NotificationType.SUCCESS,
          t("AssigmentSpecialPermission.alert.sucess")
        );

        clearAll();
      } else {
        showNotifications(
          NotificationType.ERROR,
          t("AssigmentSpecialPermission.alert.error") + response.message
        );
      }
    } catch (error) {
      showNotifications(
        NotificationType.ERROR,
        t("AssigmentSpecialPermission.alert.error")
      );
    } finally {
      setSearchTerm("")
      setIsSubmitting(false);
    }
  };

  return {
    SpecialPermissionsData: SpecialPermissions?.data,
    permissions,
    selectedPermission,
    handleSubmit,
    handleSwitchChange,
    setSelectedPermission,
    isSubmitting,
    filteredPermissions,
    searchTerm,
    setSearchTerm,
    isFormDisabled,
  };
};